import ToggleSwitch from '../../../../components/ToggleSwitch';
import AISetting from '../../../../assets/images/icons/ai-settings.svg';
import AIAnalyze from '../../../../assets/images/icons/ai-analyzing.svg';
import WaveIcon from '../../../../assets/images/icons/wave.svg';
import { useEffect, useRef, useState } from 'react';
import { AdjustSettingsModal } from './components/AdjustSettingsModal';
import { useDispatch, useSelector } from 'react-redux';
import { clearUploadData, createMasteringPreview, downloadMasterTrack } from '../../../../redux/actions/audioEngineer';
import ProcessingLoader from './components/ProcessingLoader';
import { AudioEngineerPackModal } from '../AudioEngineer/AudioEngineerPackModal';
import DropdownMenu from '../../../../components/DropdownMenu';
import { Download, Music } from 'lucide-react';
import { Tooltip } from '../../../../components/Tooltip';

const formatDuration = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = Math.floor(secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

let previewDispatched = false;

export default function MasteringEngineer({ isMaster, selectedGenreOrStyle }) {
    const dispatch = useDispatch();
    const audioRef = useRef(null);
    const hasDispatchedPreview = useRef(false);
    const hasCleared = useRef(false);

    const [isUploading, setIsUploading] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [showAudioEngineerModal, setShowAudioEngineerModal] = useState(false);

    // Separate toggle states
    const [isAfter, setIsAfter] = useState(true);           // true = After (preview), false = Before (original)
    const [loudnessMatch, setLoudnessMatch] = useState(true);

    const [adjustSettingsModal, setAdjustSettingsModal] = useState(false);

    const { uploadedData } = useSelector((state) => state.audioEngineer);
    const preview = useSelector((state) => state.audioEngineer.preview);

    const fileData = uploadedData?.data?.[0];
    const localGenre = localStorage.getItem('genre');
    const localLoudness = localStorage.getItem('loudness');
    const localSampleRate = localStorage.getItem('sampleRate');

    // Resolve the preview data — could be an array or single object
    const previewData = Array.isArray(preview) ? preview[0] : preview;
    const previewUrl = previewData?.previewUrl || previewData?.masteredUrl || previewData?.roex_response?.download_url_mastered_preview;
    const originalUrl = previewData?.originalUrl || fileData?.originalUrl;

    // The active audio source depends on the Before/After toggle
    const activeAudioUrl = isAfter ? previewUrl : originalUrl;

    const VALID_STYLES = ['ROCK_INDIE', 'POP', 'ACOUSTIC', 'HIPHOP_GRIME', 'ELECTRONIC', 'REGGAE_DUB', 'METAL', 'OTHER'];

    const upperStyle = selectedGenreOrStyle?.toUpperCase().replace('HIP_HOP_GRIME', 'HIPHOP_GRIME');

    // Trigger mastering preview when conditions are met
    useEffect(() => {
        if (
            !previewDispatched &&
            !isMaster &&
            fileData?.roexStatus === 'UPLOADED_TO_ROEX'
        ) {
            previewDispatched = true;
            const options = {
                musicalStyle: VALID_STYLES.includes(upperStyle) ? upperStyle : localGenre,
                desiredLoudness: localLoudness,
                sampleRates: [localSampleRate],
            };
            const payload = {
                fileUrls: [fileData?.readable_url],
                uploadIds: [fileData?._id],
                options,
            };
            dispatch(createMasteringPreview('', payload, setIsUploading, false));
        }
    }, [isMaster, selectedGenreOrStyle, uploadedData]);

    // previewUrl becomes available for the first time
    useEffect(() => {
        if (previewUrl && audioRef.current) {
            audioRef.current.src = previewUrl;
            audioRef.current.load();
        }
    }, [previewUrl]);

    useEffect(() => {
        return () => {
            previewDispatched = false;
        };
    }, []);

    // useEffect(() => {
    //     if (!hasCleared.current && preview) {
    //         hasCleared.current = true;
    //         dispatch(clearUploadData());
    //     }
    // }, [preview]);

    // Swap audio source when Before/After toggle changes
    useEffect(() => {
        if (!audioRef.current || !activeAudioUrl) return;

        const wasPlaying = isPlaying;
        const currentTime = audioRef.current.currentTime;

        audioRef.current.pause();
        audioRef.current.src = activeAudioUrl;
        audioRef.current.load();

        if (wasPlaying) {
            audioRef.current.currentTime = Math.min(currentTime, audioRef.current.duration || 0);
            audioRef.current.play()
                .then(() => setIsPlaying(true))
                .catch(() => setIsPlaying(false));
        }
    }, [isAfter]);

    // Play / Pause toggle
    const handlePlayPause = () => {
        if (!audioRef.current || !activeAudioUrl) return;

        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            if (!audioRef.current.src || audioRef.current.src !== activeAudioUrl) {
                audioRef.current.src = activeAudioUrl;
                audioRef.current.load();
            }
            audioRef.current.play()
                .then(() => setIsPlaying(true))
                .catch(() => setIsPlaying(false));
        }
    };

    // Sync isPlaying state when audio ends naturally
    const handleAudioEnded = () => setIsPlaying(false);

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    const getFullTrack = () => {
        setShowAudioEngineerModal(true)
    }

    const downloadAudioFile = (url, format, fileName) => {
        const reqBody = {
            url: url,
            format: format,
            fileName: fileName,
        }
        dispatch(downloadMasterTrack(reqBody, () => {}))
    }

    const getDownloadItems = () => [
        {
            label: "Download as WAV file",
            icon: <Music size={16} />,
            onClick: () => {
                const url = previewData?.masteredUrl;
                const name = previewData?.filename;
                return downloadAudioFile(url, "wav", name);
            },
        },
        {
            label: "Download as MP3 file",
            icon: <Music size={16} />,
            onClick: () => {
                const url = previewData?.masteredUrl;
                const name = previewData?.filename;
                return downloadAudioFile(url, 'mp3', name);
            },
        },
        {
            label: "Download as M4A file",
            icon: <Music size={16} />,
            onClick: () => {
                const url = previewData?.masteredUrl;
                const name = previewData?.filename;
                return downloadAudioFile(url, 'm4a', name);
            },
        },
        {
            label: "Download as AAC file",
            icon: <Music size={16} />,
            onClick: () => {
                const url = previewData?.masteredUrl;
                const name = previewData?.filename;
                return downloadAudioFile(url, 'aac', name);
            },
        },
        {
            label: "Download as OGG file",
            icon: <Music size={16} />,
            onClick: () => {
                const url = previewData?.masteredUrl;
                const name = previewData?.filename;
                return downloadAudioFile(url, 'ogg', name);
            },
        },
        {
            label: "Download as AIFF file",
            icon: <Music size={16} />,
            onClick: () => {
                const url = previewData?.masteredUrl;
                const name = previewData?.filename;
                return downloadAudioFile(url, 'aiff', name);
            },
        }
    ];

    return (
        <>
            {/* Hidden audio element */}
            <audio ref={audioRef} onEnded={handleAudioEnded} onLoadedMetadata={handleLoadedMetadata} />

            <div className="info-wrapper mt-4 pdf-exclude">
                {isUploading
                    ? <ProcessingLoader />
                    : (
                        <>
                            <div className="heading-wrapper mb-3">
                                <div className="heading-title font-inter font-extrabold text-xl space-x-2 mb-1">
                                    <span
                                        className="bg-gradient-to-r from-theme_clr2 via-theme_clr4 to-theme_clr1 bg-clip-text text-transparent"
                                        style={{ WebkitTextStroke: '0.5px #fff' }}
                                    >
                                        Mastering
                                    </span>
                                    <span className="text-transparent" style={{ WebkitTextStroke: '1px #fa6138' }}>
                                        engineer
                                    </span>
                                    <Tooltip
                                        text="If you are ready to release, get your final master for this mix."
                                        style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-64" }}
                                        position='right'
                                    >
                                        <i className="bi bi-info-circle text-gray-400"></i>
                                    </Tooltip>
                                </div>
                                <div className="heading-desc font-roboto text-white/50 text-[13px]">
                                    AI-powered enhancement for release-ready audio
                                </div>
                            </div>

                            <div className="bg-[linear-gradient(138deg,_#fa6138,_#00067e_62%)] border border-white rounded-3xl p-4">
                                <div className="mb-3">
                                    <span className="bg-transparent border border-theme_clr1 font-roboto font-semibold rounded-full text-[11px] text-theme_clr1 px-3 py-1">
                                        30s PREVIEW
                                    </span>
                                </div>

                                <div className="flex items-center justify-center gap-4 text-center mb-6">
                                    <button
                                        className="bg-white text-black rounded-full min-w-10 w-10 h-10 flex items-center justify-center disabled:opacity-40"
                                        onClick={handlePlayPause}
                                        disabled={!activeAudioUrl}
                                    >
                                        {isPlaying ? (
                                            <i className="text-2xl bi bi-pause-fill leading-tight"></i>
                                        ) : (
                                            <i className="text-2xl bi bi-play-fill leading-tight"></i>
                                        )}
                                    </button>
                                    <img src={WaveIcon} alt="Wave Icon" className="max-w-[600px] w-full" />
                                    <span className="text-sm text-white/50">{formatDuration(duration)}</span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-1 gap-4 mb-6">
                                    {/* Before / After toggle */}
                                    <div className="bg-theme_clr1 border border-theme_clr2 font-inter font-semibold flex justify-center items-center gap-3 rounded-xl text-theme_clr2 text-sm p-4 mx-48">
                                        <span>
                                            {isAfter ? 'After' : 'Before'}
                                        </span>
                                        <ToggleSwitch
                                            enabled={isAfter}
                                            setEnabled={setIsAfter}
                                            size="md"
                                        />
                                    </div>

                                    {/* Loudness Match toggle */}
                                    {/* <div className="bg-theme_clr1 border border-theme_clr2 font-inter font-semibold flex items-center gap-6 rounded-xl text-theme_clr2 text-sm p-4">
                                        <span className="lg:me-[25%]">Loudness Match</span>
                                        <ToggleSwitch
                                            enabled={loudnessMatch}
                                            setEnabled={setLoudnessMatch}
                                            size="md"
                                        />
                                    </div> */}
                                </div>

                                <div className="grid grid-cols-6 gap-4">
                                    <div className="col-start-1 col-end-7 lg:col-end-3" data-tour="adjust-settings">
                                        <div
                                            className="bg-theme_clr1 hover:bg-theme_clr2 border border-theme_clr2 font-inter font-semibold flex items-center justify-center gap-2 cursor-pointer rounded-full text-sm p-4"
                                            onClick={() => setAdjustSettingsModal(true)}
                                        >
                                            <img src={AISetting} alt="Button Icon" />
                                            <span>Adjust</span>
                                        </div>
                                    </div>
                                    <div className="col-start-1 lg:col-start-5 col-end-7">
                                        {previewData?.isMastered ? (
                                            <DropdownMenu
                                                button={
                                                    <button className="bg-gradient-to-r from-theme_clr2 via-theme_clr1 to-theme_clr4 bg-theme_clr1 border border-theme_clr2 font-inter font-semibold flex items-center justify-center gap-2 cursor-pointer rounded-full text-sm p-4">
                                                        <span className="flex items-center justify-center h-6 w-6">
                                                            <Download size={16} /> {/* smaller icon */}
                                                        </span>
                                                        Download Master
                                                    </button>
                                                }
                                                fullWidth={true}
                                                items={getDownloadItems()}
                                            />
                                        ) : (
                                            <div onClick={getFullTrack} data-tour="full-track" className="bg-gradient-to-r from-theme_clr2 via-theme_clr1 to-theme_clr4 bg-theme_clr1 border border-theme_clr2 font-inter font-semibold flex items-center justify-center gap-2 cursor-pointer rounded-full text-sm p-4">
                                                <img src={AIAnalyze} alt="div Icon" />
                                                <span>Get full track</span>
                                            </div>

                                            // <button onClick={handleMasterCreation} disabled={isProcessing} className="mt-6 w-full max-w-md bg-[#FA6138] text-white py-3 rounded-xl hover:bg-[#ff724d] transition-colors font-medium">
                                            //     {isProcessing ? 'Processing your Mastered Track(s)...' : 'Get your release-ready master'}
                                            // </button>
                                        )}

                                    </div>
                                </div>
                            </div>
                        </>
                    )
                }
            </div>

            <AdjustSettingsModal
                open={adjustSettingsModal}
                onClose={() => setAdjustSettingsModal(false)}
                selectedGenreOrStyle={selectedGenreOrStyle}
                fileData={fileData}
                setLoading={setIsUploading}
            />

            <AudioEngineerPackModal
                isOpen={showAudioEngineerModal}
                onClose={() => setShowAudioEngineerModal(false)}
                // masterRequestData={{
                //     fileName,
                //     fileUrl: originalUrl,
                //     inputUri,
                //     genre,
                //     loudness,
                //     sampleRate,
                //     format
                // }}
                setIsProcessing={setIsUploading}
            />
        </>
    );
}